import type { ResourceType } from "@store/api/resourceTypes";

export const PRODUCT_NAME_TOOLKIT = "AI Toolkit";

// Table defaults
export const DEFAULT_PAGE_LIMIT = 25;
/** Delay after typing pauses before the value is propagated. */
export const SEARCH_DEBOUNCE_MS = 300;
// Page routes
// Data explorer
export const DATA_EXPLORER_ROUTE = "/apps/data_explorer";
export const DATA_EXPLORER_PAGE_TITLE = "Data Explorer";
export const DATA_DETAIL_SEGMENT = "data";
export const DATA_TREE_SEGMENT = "tree";

export function getDataDetailUrl(dataIdentifier: string): string {
  return `${DATA_EXPLORER_ROUTE}/${DATA_DETAIL_SEGMENT}/${dataIdentifier}`;
}

export function getDataTreeUrl(
  dataIdentifier: string,
  filepath: string,
): string {
  return `${getDataDetailUrl(dataIdentifier)}/${DATA_TREE_SEGMENT}/${filepath}`.replace(
    /\/+/g,
    "/",
  );
}

// Create new job
export const CREATE_NEW_JOB_ROUTE = "/apps/jobs/new";
export const CREATE_JOB_PAGE_TITLE = "Launch a new job";

// Jobs
export const JOBS_ROUTE = "/apps/jobs";
export const JOBS_PAGE_TITLE = "Jobs";
export const JOBS_OVERVIEW_ROUTE = "/apps/overview";
export const JOB_DETAIL_SEGMENT = "job";
export const JOB_LOGS_SEGMENT = "logs";

export function getJobDetailUrl(jobIdentifier: string): string {
  return `${JOBS_ROUTE}/${JOB_DETAIL_SEGMENT}/${jobIdentifier}`;
}

export function getJobLogsUrl(jobIdentifier: string): string {
  return `${getJobDetailUrl(jobIdentifier)}/${JOB_LOGS_SEGMENT}`;
}

// Jupyter notebooks
export const NOTEBOOKS_ROUTE = "/apps/notebooks";
export const NEW_NOTEBOOKS_ROUTE = "/apps/notebooks/new";
export const NOTEBOOKS_PAGE_TITLE = "Jupyter Notebooks";

// Permissions
export const PERMISSIONS_ROUTE = "/apps/permissions";
export const EXPIRING_SOON_DAYS = 7;

// Access requests
export const ACCESS_REQUESTS_ROUTE = "/apps/access-requests";
export const NEW_ACCESS_REQUEST_ROUTE = "/apps/access-requests/new";
export const ACCESS_REQUESTS_PAGE_TITLE = "Access Requests";
export const ACCESS_REQUEST_DETAIL_SEGMENT = "access-request";

// Builds the URL for the new access request form, optionally prefilled with a
// resource id or name. Pass the type when known: a name can be shared by
// resources of different types, and the type tells the form which one is meant.
export function getAccessRequestUrl(
  resourceIdentifier?: string | null,
  type?: ResourceType,
): string {
  if (!resourceIdentifier) return NEW_ACCESS_REQUEST_ROUTE;
  const typeParam = type ? `&type=${type}` : "";
  return `${NEW_ACCESS_REQUEST_ROUTE}?resource=${encodeURIComponent(resourceIdentifier)}${typeParam}`;
}

// API Docs
export const API_DOCS_ROUTE = "/api-docs";

// Assistant
export const ASSISTANT_ROUTE = "/apps/assistant";
export const ASSISTANT_PAGE_TITLE = "Assistant";

// Toolkit docs links
export const EAI_CONSOLE_URL =
  process.env.EAI_CONSOLE_URL || window.location.origin;

export const { host: EAI_CONSOLE_HOST, protocol: EAI_CONSOLE_PROTOCOL } =
  new URL(EAI_CONSOLE_URL);

// Local storage keys
export const LOCAL_STORAGE_KEY_GET_STARTED_PANEL_HIDDEN =
  "get_started_panel_hidden";
export const LOCAL_STORAGE_KEY_GET_STARTED_PANEL_CLOSED =
  "get_started_panel_closed";
export const LOCAL_STORAGE_KEY_JOBS_FILTER = "jobs_filter_preference";
export const LOCAL_STORAGE_KEY_JOBS_DATE_RANGE = "jobs_date_range_preference";
export const LOCAL_STORAGE_KEY_DATA_FILTER = "data_filter_preference";
export const TAG_KEY_STORAGE_KEY = "customTagKey";
export const DEFAULT_TAG_KEY = "projectcode";
export const LOCAL_STORAGE_KEY_DATA_DATE_RANGE = "data_date_range_preference";
export const LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_ACCOUNTS =
  "recently_chosen_accounts";
export const LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_IMAGES =
  "recently_chosen_images_for_job_submit";
export const LOCAL_STORAGE_KEY_USER_THEME = "orkaview-user-theme";
export const LOCAL_STORAGE_KEY_SKIP_JOB_CANCEL_CONFIRMATION =
  "skipJobCancelConfirmation";
export const LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_DATA_OBJECTS =
  "recently_chosen_data_objects_for_job_submit";
export const LOCAL_STORAGE_KEY_EAI_TOOLKIT_LOCAL_STATE =
  "eai_toolkit_local_state";
export const LOCAL_STORAGE_KEY_MENU_FIXED = "EAI_menu_fixed";

// Spacing constants
export const COPY_ICON_SPACING = "24px";
